import React from "react";
import {
  Button,
  ButtonContext,
  FieldErrorContext,
  ListStateContext,
  OverlayTriggerStateContext,
  useSlottedContext,
} from "react-aria-components";
import { XCircle as LineXCircle } from "../../icons/line.tsx";
import { XCircle as SolidXCircle } from "../../icons/solid.tsx";
import useI18n from "../internal/useI18n.ts";
import * as styles from "./ClearButton.css.ts";

function useClearButtonState(
  hasSelection: boolean,
  isInvalid: boolean,
  isOpen: boolean,
) {
  const [isFakeOpen, setIsFakeOpen] = React.useState(false);
  const wasOpenRef = React.useRef(false);

  // If we're in the isInvalid state the clear button is only visible when the combobox is open, and as such
  // when the combobox closes it disappears. But, if the user clicks on the clear button then the combobox
  // first closes, so we need to delay hiding the clear button a little, in case the user actually clicked on it.
  React.useEffect(
    function delayedClosing() {
      if (!isOpen && wasOpenRef.current) {
        wasOpenRef.current = false;
        const timeout = setTimeout(() => {
          setIsFakeOpen(false);
        }, 200);

        return () => {
          clearTimeout(timeout);
        };
      }
      if (!isOpen || wasOpenRef.current) {
        return;
      }

      wasOpenRef.current = true;
      requestAnimationFrame(() => {
        setIsFakeOpen(true);
      });
    },
    [isOpen],
  );

  const isListBoxOpen = isOpen || isFakeOpen;
  let visible: boolean | "delayed";
  if (isInvalid) {
    visible = isFakeOpen && !isListBoxOpen ? "delayed" : isListBoxOpen;
  } else {
    visible = hasSelection;
  }

  return visible;
}

interface Props {
  clearButton: boolean | string;

  isOpen?: boolean;
  hasSelection?: boolean;
  onClear?: () => void;
}

export const ClearButton: React.FC<Props> = ({
  clearButton,

  isOpen: isOpenProp = false,
  hasSelection: hasSelectionProp = false,
  onClear,
}) => {
  const isOverlayOpen =
    React.use(OverlayTriggerStateContext)?.isOpen === true || isOpenProp;
  const i18n = useI18n();

  const listState = React.use(ListStateContext);
  const hasSelection =
    listState?.selectionManager.firstSelectedKey != null || hasSelectionProp;

  const errorState = React.use(FieldErrorContext);
  const isInvalid = errorState?.isInvalid === true;

  const visible = useClearButtonState(hasSelection, isInvalid, isOverlayOpen);
  const isFieldDisabled = useSlottedContext(ButtonContext)?.isDisabled === true;

  if (clearButton === false || isFieldDisabled) {
    return null;
  }

  return (
    <Button
      // Don't inherit default Button behavior from ComboBox/Select.
      slot={null}
      onPress={() => {
        if (listState) {
          listState.selectionManager.setSelectedKeys(new Set());
        } else {
          onClear?.();
        }
      }}
      className={styles.clearButton({ isVisible: visible })}
      aria-label={
        typeof clearButton === "string"
          ? clearButton
          : i18n.format("clearButton")
      }
      isDisabled={visible === false}
    >
      {({ isFocused }) =>
        isFocused ? (
          <SolidXCircle className={styles.clearButtonIcon} />
        ) : (
          <LineXCircle className={styles.clearButtonIcon} />
        )
      }
    </Button>
  );
};
